import React from "react";
import { Box } from "@mui/material";

/**
 * PageContent - Reusable wrapper component that ensures pages fit within available viewport height
 * 
 * Provides:
 * - Flex-based height constraints
 * - Consistent spacing
 * - Optional PageHeader integration
 * - Mobile-responsive padding
 * - Proper overflow handling
 * - Works seamlessly with DataGrid components (provides constrained container)
 * 
 * @param {React.ReactNode} children - Page content
 * @param {React.ReactNode} header - Optional PageHeader component
 * @param {boolean} fullHeight - Boolean for pages that need full height (like Messages detail view)
 * @param {boolean} disablePadding - Boolean to remove default padding
 * @param {boolean} scrollable - Boolean to enable/disable scrolling (default: true)
 */
const PageContent = ({
  children,
  header,
  fullHeight = false,
  disablePadding = false,
  scrollable = true,
}) => {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        flex: 1,
        overflow: "hidden",
        ...(fullHeight && {
          minHeight: 0, // Allow flex shrinking
        }),
      }}
    >
      {header && (
        <Box
          sx={{
            flexShrink: 0,
          }}
        >
          {header}
        </Box>
      )}
      
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0, // Important for flex children to shrink properly
          overflow: scrollable ? "auto" : "hidden",
          ...(!disablePadding && {
            px: { xs: 2, sm: 3 },
            py: 2,
          }),
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default PageContent;
